/**
 * <s-header-overlay> — turns the homepage's transparent header solid once it sticks.
 *
 * Markup: render this element (empty, 1px tall) directly above the sticky
 * `<header class="header-overlay">`. Its document offset is where the header starts
 * sticking (the bottom of the announcement bar). The component compares the page
 * scroll position with that offset and sets `data-solid` on the header once it is
 * reached. It reads the scroll position rather than viewport visibility so it also
 * works when the storefront is shown inside an editor frame. config/styles.css
 * ("TRANSPARENT HEADER") styles both states and the transition between them.
 */
class SHeaderOverlay extends HTMLElement {
  #ac;
  #header = null;
  #threshold = 0;
  #frame = 0;

  connectedCallback() {
    this.#header = document.querySelector('header.header-overlay');
    if (!this.#header) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, passive: true };

    this.#measure();
    window.addEventListener('scroll', () => this.#schedule(), opts);
    window.addEventListener('resize', () => { this.#measure(); this.#schedule(); }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #measure() {
    this.#threshold = Math.max(0, this.getBoundingClientRect().top + window.scrollY);
    this.#update();
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    this.#header?.toggleAttribute('data-solid', window.scrollY > this.#threshold);
  }
}

if (!customElements.get('s-header-overlay')) {
  customElements.define('s-header-overlay', SHeaderOverlay);
}
